// Searchable single-select dropdown.
//
// Replaces native <select> in the app. Pattern is mirrored from
// cherie-app/client/components/shared/SearchableSelect.tsx but rebuilt
// without Radix/cmdk because this project doesn't have those primitives.
// Behaviour to preserve:
//
//   * Multi-term search — every whitespace-separated term must match
//     somewhere in the option's label or extra string (case-insensitive).
//   * Yellow highlighting of every matched span (merged so overlapping
//     terms don't double-wrap).
//   * Click-outside / Escape close the menu, with focus returning to the
//     trigger.
//   * Up/Down arrow keys move the active item; Enter selects; Home/End
//     jump to first/last.
//   * Selecting the already-selected item clears the value (the same
//     "click again to clear" toggle behaviour as cherie).
//   * Optional clearable affordance via a Clear button at the top of the
//     list, plus a placeholder "no value" state.
//
// Styling matches the project's standard inputClass (cream border, copper
// focus) so callers don't have to restyle every site that adopts it.

import { Check, ChevronDown, Search, X } from "lucide-react";
import type { ReactNode } from "react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

export interface SearchableSelectOption {
  // Internal value persisted to form state. Must be unique within `options`.
  value: string;
  // What the user sees and what the search matches against.
  label: string;
  // Optional secondary text shown muted to the right of the label. Also
  // included in the search haystack and highlighted on match.
  extra?: string;
  // Optional small left-side icon for the row (e.g., a coloured dot or
  // lucide icon). Rendered as-is.
  icon?: ReactNode;
  // Optional hint shown under the label on its own line in the dropdown.
  // Not included in the search haystack to keep behaviour predictable.
  hint?: string;
  // When true, the row is unselectable (greyed out, ignored by keyboard nav).
  disabled?: boolean;
}

export interface SearchableSelectProps {
  // Controlled value. "" or null mean "nothing selected".
  value: string | null | undefined;
  onChange: (next: string | null) => void;
  options: SearchableSelectOption[];
  // Placeholder text for the trigger when nothing is selected.
  placeholder?: string;
  // Placeholder text inside the search input.
  searchPlaceholder?: string;
  // When provided, a top-of-list "Clear" button restores `null`. Useful for
  // nullable fields (e.g., difficulty_level).
  clearable?: boolean;
  clearLabel?: string;
  disabled?: boolean;
  // Additional class names merged into the trigger button so callers can
  // match an existing inputClass without us hard-coding theirs.
  className?: string;
  // Marks the trigger as invalid for parent forms (red ring).
  error?: boolean;
  // Optional id for the trigger button (used by checklist "Fix it" scroll).
  id?: string;
  // Optional aria label when no visible <label> wraps the trigger.
  ariaLabel?: string;
  // Optional copy shown when the search returns no matches.
  emptyLabel?: string;
}

// Split a query into trimmed lowercase terms. "  red  big " → ["red","big"].
function tokenize(query: string): string[] {
  return query.toLowerCase().trim().split(/\s+/).filter(Boolean);
}

// Return non-overlapping ranges of `text` covered by any of `terms`.
function highlightRanges(
  text: string,
  terms: string[],
): Array<{ start: number; end: number }> {
  if (terms.length === 0 || !text) return [];
  const lower = text.toLowerCase();
  const ranges: Array<{ start: number; end: number }> = [];
  for (const term of terms) {
    if (!term) continue;
    let i = 0;
    while (i < lower.length) {
      const pos = lower.indexOf(term, i);
      if (pos === -1) break;
      ranges.push({ start: pos, end: pos + term.length });
      i = pos + 1;
    }
  }
  if (ranges.length === 0) return [];
  ranges.sort((a, b) => a.start - b.start);
  const merged: Array<{ start: number; end: number }> = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && r.start <= last.end) {
      last.end = Math.max(last.end, r.end);
    } else {
      merged.push({ ...r });
    }
  }
  return merged;
}

function HighlightedText({ text, terms }: { text: string; terms: string[] }) {
  const ranges = highlightRanges(text, terms);
  if (ranges.length === 0) return <>{text}</>;
  const parts: ReactNode[] = [];
  let cursor = 0;
  ranges.forEach((range, idx) => {
    if (cursor < range.start) {
      parts.push(
        <span key={`p-${idx}`}>{text.slice(cursor, range.start)}</span>,
      );
    }
    parts.push(
      <mark
        key={`m-${idx}`}
        className="rounded-sm bg-yellow-200 px-0.5 font-semibold text-yellow-900"
      >
        {text.slice(range.start, range.end)}
      </mark>,
    );
    cursor = range.end;
  });
  if (cursor < text.length) {
    parts.push(<span key="p-end">{text.slice(cursor)}</span>);
  }
  return <>{parts}</>;
}

const TRIGGER_BASE =
  "flex w-full items-center justify-between gap-2 rounded-md border bg-white px-3 py-2 text-left text-sm text-slate-900 shadow-sm transition-colors focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50";

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "Choose an option",
  searchPlaceholder,
  clearable = false,
  clearLabel = "Clear selection",
  disabled = false,
  className,
  error = false,
  id,
  ariaLabel,
  emptyLabel = "No matches",
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const listboxId = useId();

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  const terms = useMemo(() => tokenize(query), [query]);

  const filtered = useMemo(() => {
    if (terms.length === 0) return options;
    return options.filter((o) => {
      const haystack = `${o.label.toLowerCase()} ${o.extra?.toLowerCase() ?? ""}`;
      return terms.every((t) => haystack.includes(t));
    });
  }, [options, terms]);

  // Reset activeIndex whenever the filtered list shape changes so keyboard
  // nav always starts from a visible row.
  useEffect(() => {
    setActiveIndex(0);
  }, [filtered.length, open]);

  // Click outside closes the menu. We listen on mousedown so a click on an
  // option inside the menu fires before we tear it down.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (containerRef.current.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // When the menu opens, focus the search input. When it closes, return
  // focus to the trigger so keyboard users don't lose their place.
  useEffect(() => {
    if (open) {
      // Defer one tick so the input is mounted.
      const t = window.setTimeout(() => searchRef.current?.focus(), 0);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open]);

  // Keep the active option scrolled into view as the user arrows through.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const li = listRef.current.querySelector<HTMLLIElement>(
      `[data-index="${activeIndex}"]`,
    );
    li?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  const closeAndRefocus = useCallback(() => {
    setOpen(false);
    setQuery("");
    // Defer so React commits the state change before we touch the DOM.
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  }, []);

  const selectAt = useCallback(
    (idx: number) => {
      const opt = filtered[idx];
      if (!opt || opt.disabled) return;
      // Toggle: clicking the already-selected row clears it (mirrors cherie).
      const next = opt.value === value ? null : opt.value;
      onChange(next);
      closeAndRefocus();
    },
    [filtered, onChange, value, closeAndRefocus],
  );

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
    }
  };

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeAndRefocus();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => {
        for (let step = 1; step <= filtered.length; step++) {
          const next = (i + step) % Math.max(filtered.length, 1);
          if (!filtered[next]?.disabled) return next;
        }
        return i;
      });
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => {
        for (let step = 1; step <= filtered.length; step++) {
          const next =
            (i - step + filtered.length) % Math.max(filtered.length, 1);
          if (!filtered[next]?.disabled) return next;
        }
        return i;
      });
      return;
    }
    if (e.key === "Home") {
      e.preventDefault();
      setActiveIndex(filtered.findIndex((o) => !o.disabled));
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      for (let i = filtered.length - 1; i >= 0; i--) {
        if (!filtered[i]?.disabled) {
          setActiveIndex(i);
          return;
        }
      }
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      selectAt(activeIndex);
      return;
    }
  };

  const showClear = clearable && value != null && value !== "";

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        id={id}
        ref={triggerRef}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={ariaLabel}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={handleTriggerKeyDown}
        className={`${TRIGGER_BASE} ${
          error
            ? "border-red-400 focus:border-red-500 focus:ring-red-300/40"
            : "border-[#e8c9a2] focus:border-[#B87333] focus:ring-[#B87333]/20"
        } ${className ?? ""}`}
      >
        <span
          className={`flex min-w-0 flex-1 items-center gap-2 truncate ${
            selected ? "" : "text-slate-400"
          }`}
        >
          {selected?.icon ? (
            <span className="shrink-0">{selected.icon}</span>
          ) : null}
          <span className="truncate">
            {selected ? selected.label : placeholder}
          </span>
          {selected?.extra ? (
            <span className="truncate text-xs text-slate-500">
              · {selected.extra}
            </span>
          ) : null}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      </button>

      {open && !disabled ? (
        <div
          // Anchored to the trigger; sticks below it. z-50 keeps it above
          // sticky headers and the right-column sidebar.
          className="absolute left-0 right-0 z-50 mt-1 overflow-hidden rounded-md border border-[#e8c9a2] bg-white shadow-lg ring-1 ring-black/5"
          onKeyDown={handleListKeyDown}
        >
          <div className="flex items-center gap-2 border-b border-[#f4e4cf] bg-[#fdf6ec]/40 px-2 py-1.5">
            <Search
              className="h-4 w-4 shrink-0 text-[#8a531f]/70"
              aria-hidden
            />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder ?? "Search…"}
              className="w-full bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
              autoComplete="off"
              spellCheck={false}
            />
            {query ? (
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  searchRef.current?.focus();
                }}
                className="rounded p-0.5 text-slate-400 hover:bg-[#fdf6ec] hover:text-slate-700"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>

          {showClear ? (
            <button
              type="button"
              onClick={() => {
                onChange(null);
                closeAndRefocus();
              }}
              className="block w-full border-b border-[#f4e4cf] px-3 py-1.5 text-left text-xs font-semibold text-slate-500 hover:bg-[#fdf6ec]"
            >
              {clearLabel}
            </button>
          ) : null}

          <ul
            id={listboxId}
            ref={listRef}
            role="listbox"
            tabIndex={-1}
            className="max-h-64 overflow-y-auto py-1"
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-slate-500">{emptyLabel}</li>
            ) : (
              filtered.map((opt, idx) => {
                const isSelected = opt.value === value;
                const isActive = idx === activeIndex;
                return (
                  <li
                    key={opt.value}
                    data-index={idx}
                    role="option"
                    aria-selected={isSelected}
                    aria-disabled={opt.disabled || undefined}
                    onMouseDown={(e) => {
                      // Prevent the trigger from blurring before onClick fires.
                      e.preventDefault();
                    }}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => selectAt(idx)}
                    className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm ${
                      opt.disabled
                        ? "cursor-not-allowed opacity-50"
                        : isActive
                          ? "bg-[#fdf6ec]"
                          : ""
                    } ${isSelected ? "font-semibold text-[#8a531f]" : "text-slate-800"}`}
                  >
                    {opt.icon ? (
                      <span className="shrink-0">{opt.icon}</span>
                    ) : null}
                    <span className="min-w-0 flex-1 truncate">
                      <HighlightedText text={opt.label} terms={terms} />
                      {opt.hint ? (
                        <span className="block text-xs text-slate-500">
                          {opt.hint}
                        </span>
                      ) : null}
                    </span>
                    {opt.extra ? (
                      <span className="shrink-0 truncate text-xs text-slate-500">
                        <HighlightedText text={opt.extra} terms={terms} />
                      </span>
                    ) : null}
                    {isSelected ? (
                      <Check
                        className="h-4 w-4 shrink-0 text-[#8a531f]"
                        aria-hidden
                      />
                    ) : null}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
